import { createStyles } from 'antd-style'

export const useStyles = createStyles(({ token, prefixCls }) => ({
  /**
   * The element `BorderBeam` decorates: it resolves a real DOM node from its
   * child, inserts the beam as an absolutely positioned layer, and inherits the
   * radius — so this host owns `position: relative` and the pill's radius, and
   * the button inside stays a plain button.
   */
  beamHost: {
    position: 'relative',
    display: 'flex',
    borderRadius: token.borderRadiusLG,
    // The footer's other row (the collapse toggle) sits directly below.
    marginBlockEnd: token.marginXXS
  },

  beamHostCollapsed: {
    width: 30,
    marginInline: 'auto'
  },

  /**
   * Retires the beam without unmounting `BorderBeam` — swapping the wrapper out
   * would remount the button it decorates and close an open panel with it. Same
   * technique as antd's own "show on hover" demo, including pausing the animation
   * so an invisible beam stops costing frames.
   */
  beamOff: {
    [`& .${prefixCls}-border-beam`]: {
      opacity: 0,
      transition: `opacity ${token.motionDurationSlow}`,

      '&::before': {
        animationPlayState: 'paused'
      }
    }
  },

  pill: {
    width: '100%',
    height: 30,
    justifyContent: 'center',
    gap: token.marginXXS,
    paddingInline: token.paddingXS,
    borderRadius: token.borderRadiusLG,
    border: `1px solid ${token.colorBorderSecondary}`,
    background: token.colorBgContainer,
    fontSize: 12,

    '&:hover': {
      borderColor: token.colorPrimaryBorderHover
    }
  },

  pillCollapsed: {
    width: 30,
    paddingInline: 0
  },

  /**
   * antd wraps a Button's children in one `<span>`, so the Button's own `gap`
   * never lands between the label and the counter — this inner flex owns it.
   */
  pillLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: token.marginXXS
  },

  pillCount: {
    color: token.colorTextTertiary,
    fontVariantNumeric: 'tabular-nums'
  },

  /**
   * The popover's box is the list and nothing else.
   *
   * antd realigns a popover whenever its content resizes, so anything that joins
   * the flow when the pointer touches a row drags the rows out from under the
   * cursor. The card is lifted out of the flow instead (see `card`), which leaves
   * this box the size it opened at — it is here to be the card's positioning
   * context, nothing more.
   */
  panel: {
    position: 'relative',
    display: 'flex'
  },

  list: {
    flex: '0 0 232px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: token.paddingXS
  },

  listHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: token.marginXS,
    padding: `2px ${token.paddingXXS}px ${token.paddingXS}px`
  },

  listTitle: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: '18px'
  },

  listProgress: {
    fontSize: 11,
    color: token.colorTextTertiary
  },

  row: {
    display: 'flex',
    alignItems: 'center',
    gap: token.marginXS,
    width: '100%',
    padding: `${token.paddingXXS}px ${token.paddingXS}px`,
    border: 'none',
    borderRadius: token.borderRadius,
    background: 'transparent',
    color: token.colorText,
    fontSize: 13,
    lineHeight: '20px',
    textAlign: 'start',
    cursor: 'pointer'

    // No `:hover` background here on purpose: the active row is whichever one
    // React thinks is active. A second, CSS-driven highlight can disagree with it
    // (a pointer that ends up over a row without an `onMouseEnter` — say, after a
    // scroll) and then two rows look selected while only one card is shown.
  },

  rowActive: {
    background: token.colorFillTertiary
  },

  rowDone: {
    color: token.colorTextTertiary
  },

  rowIcon: {
    display: 'inline-flex',
    flex: '0 0 auto',
    color: token.colorTextTertiary
  },

  rowLabel: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },

  rowLabelDone: {
    textDecoration: 'line-through'
  },

  rowCheck: {
    display: 'inline-flex',
    flex: '0 0 auto',
    color: token.colorSuccess
  },

  /**
   * A surface of its own beside the list, not a second column inside it.
   *
   * Out of the flow, so the list keeps the size and the place it opened at: as a
   * flex child the card widened the popover by 320px the moment the pointer
   * touched a row, antd realigned to the new box, and the whole panel jumped.
   *
   * Bottoms aligned rather than tops — the pill lives at the foot of the sidebar,
   * so upwards is the direction with room, and nothing the card does can reach
   * down over the pill it belongs to.
   */
  card: {
    position: 'absolute',
    insetInlineStart: `calc(100% + ${token.marginXS}px)`,
    bottom: 0,
    // The two halves the old single 552-wide box was cut from, both still fixed: a
    // card that sized itself per task would flicker as the pointer moved down the
    // list. What keeps the buttons inside 320 is short CTA labels (see the `cta`
    // keys) plus the wrapping row in `ChecklistItemCard`, not extra width.
    width: 320,
    // Sized so the tallest card (a four-line description) fits exactly: 84px of
    // fixed rows and gaps + 72px of text + the image's 104px floor.
    height: 264,
    display: 'flex',
    flexDirection: 'column',
    gap: token.marginXS,
    overflow: 'hidden',
    // Same inset as the list column beside it.
    padding: token.paddingXS,
    borderRadius: token.borderRadiusLG,

    /**
     * Its own ground, at the popover's own elevation. antd carries the popover's
     * shadow on an ancestor as a `filter: drop-shadow`, and a filter traces every
     * painted surface beneath it — so this one is shadowed without asking.
     */
    backgroundColor: token.colorBgElevated,

    /**
     * A hint of brand warmth across the whole pane, deepening towards the corner
     * the buttons sit in. `colorPrimaryBg` rather than a hand-mixed alpha: it is
     * the token that already knows how faint the primary hue should be in either
     * theme.
     *
     * The far stop sits past the box (150%), so the tint never reaches even that
     * token's full strength anywhere in the pane — a weaker wash without mixing an
     * alpha the theme knows nothing about.
     */
    backgroundImage: `linear-gradient(140deg, transparent 0%, ${token.colorPrimaryBg} 150%)`
  },

  /**
   * Takes whatever the fixed rows leave over — which, with a one-line title and a
   * two-line `cardText`, is the same height on every card. The image is the part
   * that used to vary from task to task, and it is the part that must not.
   *
   * The floor is there for the exception: an error line pushes in above the
   * buttons, and the image gives up the height rather than the row being clipped.
   */
  cardImage: {
    display: 'block',
    flex: 1,
    width: '100%',
    minHeight: 72,
    objectFit: 'cover',
    borderRadius: token.borderRadiusLG,
    border: `1px solid ${token.colorBorderSecondary}`,
    background: token.colorFillQuaternary
  },

  /**
   * One line, always — see `cardText`. Every title fits today; the ellipsis is
   * there so a longer one ever added costs a tail rather than the card's shape.
   */
  cardTitle: {
    overflow: 'hidden',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '20px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },

  /**
   * Exactly two lines — every task, every language.
   *
   * A fixed height rather than a cap: the image takes its size from what is left
   * over, so a description that ran to three lines in one language shrank the
   * picture on that card alone and the panel stopped reading as one thing. Every
   * description is written to fit two lines at 304px / 12px — measure before
   * editing one — and the clamp is the backstop for the one that is not.
   */
  cardText: {
    flex: '0 0 auto',
    height: 36,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    fontSize: 12,
    lineHeight: '18px',
    color: token.colorTextSecondary
  },

  /**
   * Borderless docs link beside the CTA. antd keeps a link button's horizontal
   * padding, which is width this row cannot spare — see the note in
   * `ChecklistItemCard`.
   */
  cardDocsLink: {
    paddingInline: 0
  },

  reason: {
    width: '100%',
    height: 'auto',
    padding: `${token.paddingSM}px ${token.padding}px`,
    borderRadius: token.borderRadiusLG,
    // A list of options reads down the left edge; antd centres button content by
    // default, which leaves the four icons floating in the middle of the modal.
    justifyContent: 'flex-start',
    gap: token.marginXS,
    textAlign: 'start',
    whiteSpace: 'normal'
  }
}))
